import { faSpinner, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { getInternalDeliveryBudgetGetYearlyBudgetsQueryKey } from 'api/internal-delivery-budget/internal-delivery-budget';
import {
  getInternalDeliveryDailyBudgetOverrideGetDailyBudgetOverridesQueryKey,
  useInternalDeliveryDailyBudgetOverrideDeleteDailyBudgetOverride,
} from 'api/internal-delivery-daily-budget-override/internal-delivery-daily-budget-override';
import Input from 'components/inputs/Input';
import TextButton from 'components/inputs/TextButton';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useUpsertDailyBudgetOverride from './useUpsertDailyBudgetOverride';

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const Loader = styled.div`
  grid-column-end: 3;
`;

const MyInput = styled(Input)`
  padding-right: 5px;
  margin: 0;
  text-align: right;
  width: 70px;
  height: 20px;
`;

interface Props {
  value: number;
  date: Date;
  companyId: number;
  overrideId?: number;
  isManualOverride?: boolean;
  // Pass-through values so the upsert preserves sibling fields on the override row.
  transferredFromPreviousDay?: number;
  plannedAmount?: number;
  onUpdate?: () => void;
  isFocused?: boolean;
}

const OverrideDailyInternalDeliveryBudgetInput: FC<Props> = ({
  value,
  date,
  companyId,
  overrideId,
  isManualOverride,
  transferredFromPreviousDay,
  plannedAmount,
  onUpdate,
  isFocused,
}) => {
  const [cellValue, setCellValue] = useState(value);
  const [isUpdatingCell, setIsUpdatingCell] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { upsert, isLoading: isUpserting } = useUpsertDailyBudgetOverride();
  const deleteOverride =
    useInternalDeliveryDailyBudgetOverrideDeleteDailyBudgetOverride();
  const queryClient = useQueryClient();

  const handleChangeValue = useCallback(
    (val: number) => {
      if (val !== value) {
        upsert(
          {
            companyId,
            date,
            overrideId,
            amount: val,
            transferredFromPreviousDay,
            plannedAmount,
          },
          onUpdate
        );
      }
    },
    [
      value,
      upsert,
      companyId,
      date,
      overrideId,
      transferredFromPreviousDay,
      plannedAmount,
      onUpdate,
    ]
  );

  const handleDeleteOverride = useCallback(() => {
    deleteOverride.mutate(
      {
        data: {
          dailyBudgetOverrideID: overrideId ?? -1,
          companyID: companyId,
          date,
          amount: 0,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(
            getInternalDeliveryDailyBudgetOverrideGetDailyBudgetOverridesQueryKey()
          );
          queryClient.invalidateQueries(
            getInternalDeliveryBudgetGetYearlyBudgetsQueryKey()
          );
        },
      }
    );
    onUpdate?.();
  }, [companyId, date, deleteOverride, onUpdate, overrideId, queryClient]);

  useEffect(() => {
    if (isFocused) {
      setIsUpdatingCell(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => handleChangeValue(cellValue), 3000);
      setIsUpdatingCell(false);
    }
  }, [cellValue, handleChangeValue, isFocused, isUpdatingCell]);

  return (
    <Wrapper>
      <Loader>{isUpserting && <FontAwesomeIcon icon={faSpinner} spin />}</Loader>
      {isFocused && !isUpdatingCell ? (
        <>
          <MyInput
            type="number"
            disabled={isUpserting}
            value={cellValue}
            onChange={(eve) => {
              const newNum = Number(eve.target.value);
              if (!Number.isNaN(newNum) && newNum < 0) {
                setCellValue(0);
              } else {
                setCellValue(newNum);
              }
            }}
            onKeyDown={(eve) => {
              if (eve.key === 'Enter') {
                handleChangeValue(cellValue);
              }
            }}
          />
          {overrideId && (
            <TextButton
              onClick={handleDeleteOverride}
              disabled={deleteOverride.isLoading}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </TextButton>
          )}
        </>
      ) : (
        <span
          title="Budget"
          style={{ color: isManualOverride ? 'crimson' : 'black' }}
        >
          {value ?? ''}
        </span>
      )}
    </Wrapper>
  );
};

export default OverrideDailyInternalDeliveryBudgetInput;
